import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { USER_ROLES } from '@sac/contracts'
import { api, type TeamUserDTO } from '../../lib/api'
import { qk } from '../../lib/query'
import { Icon } from '../icons'
import { useToast } from '../ui/Toaster'
import { AgentAvatar } from '../AgentAvatar'
import { AvatarPicker } from '../AvatarPicker'

/**
 * Configurações → Usuários.
 *
 * Admin-only, enforced by the API rather than by hiding the tab — a role check that lives only
 * in the browser is decoration.
 *
 * A new user's password is generated server-side and returned exactly once. It is shown here in
 * a panel the admin must dismiss deliberately, because there is no way to retrieve it later:
 * storing it to display again would mean keeping a readable password.
 */

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  supervisor: 'Supervisor',
  agent: 'Atendente',
}

export function UsersTab() {
  const qc = useQueryClient()
  const toast = useToast()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('agent')
  const [photoFor, setPhotoFor] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null)

  const { data: users, isLoading, error } = useQuery({ queryKey: qk.users(), queryFn: api.users })
  const refresh = () => qc.invalidateQueries({ queryKey: qk.users() })

  const create = useMutation({
    mutationFn: () => api.createUser({ name: name.trim(), email: email.trim(), role }),
    onSuccess: (u) => {
      setIssued({ email: u.email, password: u.temporaryPassword })
      setAdding(false)
      setName('')
      setEmail('')
      setRole('agent')
      void refresh()
    },
    onError: (e: Error) => toast(e.message, true),
  })

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; email?: string; role?: string } }) =>
      api.updateUser(id, patch),
    onSuccess: () => {
      toast('Usuário atualizado')
      void refresh()
    },
    onError: (e: Error) => toast(e.message, true),
  })

  const reset = useMutation({
    mutationFn: (u: TeamUserDTO) => api.resetUserPassword(u.id).then((r) => ({ ...r, email: u.email })),
    onSuccess: (r) => setIssued({ email: r.email, password: r.temporaryPassword }),
    onError: (e: Error) => toast(e.message, true),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      toast('Usuário removido')
      void refresh()
    },
    onError: (e: Error) => toast(e.message, true),
  })

  if (isLoading) return <div className="empty">Carregando…</div>
  if (error) {
    // The API returns 403 for non-admins; saying so beats an empty list that looks like a bug.
    return (
      <div className="empty" style={{ padding: 26 }}>
        Apenas administradores podem gerenciar a equipe.
      </div>
    )
  }

  return (
    <>
      {issued && (
        <div className="cfg-issued">
          <div>
            <b>Senha temporária de {issued.email}</b>
            <div className="cfg-sub">
              Copie agora — ela não pode ser exibida de novo. Peça para a pessoa trocá-la no
              primeiro acesso.
            </div>
          </div>
          <code className="cfg-pass">{issued.password}</code>
          <button
            className="btn sec sm"
            onClick={() => {
              void navigator.clipboard?.writeText(issued.password)
              toast('Senha copiada')
            }}
          >
            Copiar
          </button>
          <button className="btn ghost sm" onClick={() => setIssued(null)}>
            Fechar
          </button>
        </div>
      )}

      <div className="cfg-block">
        <div className="blk-h">Equipe</div>

        {users?.map((u) => (
          <div className="cfg-row" key={u.id}>
            {/* Clicável: um admin troca a foto de qualquer pessoa daqui, que é o lugar onde ele
                já mexe no resto da equipe. A permissão é do servidor; isto é só o caminho. */}
            <button
              className="cfg-avbtn"
              title={`Alterar foto de ${u.name}`}
              onClick={() => setPhotoFor(photoFor === u.id ? null : u.id)}
            >
              <AgentAvatar name={u.name} color={u.avatarColor} avatarUrl={u.avatarUrl} size={30} />
            </button>
            <div className="cfg-main">
              <div className="cfg-title">
                {u.name}
                {u.isSelf && <span className="cfg-badge">você</span>}
              </div>
              <div className="cfg-sub">
                {u.email}
                {u.messageCount > 0 ? ` · ${u.messageCount} resposta(s)` : ''}
              </div>
            </div>

            <div className="cfg-actions">
              <select
                className="tk-status"
                value={u.role}
                onChange={(e) => update.mutate({ id: u.id, patch: { role: e.target.value } })}
              >
                {USER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r] ?? r}
                  </option>
                ))}
              </select>
              <button
                className="btn sec sm"
                onClick={() => {
                  const next = prompt('Novo e-mail de acesso', u.email)?.trim()
                  if (next && next !== u.email) update.mutate({ id: u.id, patch: { email: next } })
                }}
              >
                E-mail
              </button>
              <button
                className="btn sec sm"
                onClick={() => {
                  // Resetting ends their sessions, so it is worth one confirmation.
                  if (confirm(`Gerar nova senha para ${u.email}? A pessoa será desconectada.`)) reset.mutate(u)
                }}
              >
                Senha
              </button>
              {!u.isSelf && (
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    if (confirm(`Remover ${u.email}? Esta ação não pode ser desfeita.`)) remove.mutate(u.id)
                  }}
                >
                  Remover
                </button>
              )}
            </div>
            {photoFor === u.id && (
              <div className="cfg-avpanel">
                <AvatarPicker
                  userId={u.id}
                  name={u.name}
                  color={u.avatarColor}
                  avatarUrl={u.avatarUrl}
                  size={48}
                  onDone={() => setPhotoFor(null)}
                />
              </div>
            )}
          </div>
        ))}

        {adding ? (
          <div className="cfg-new" style={{ flexWrap: 'wrap' }}>
            <input placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <input placeholder="e-mail@quartinhos.com.br" value={email} onChange={(e) => setEmail(e.target.value)} />
            <select className="tk-status" value={role} onChange={(e) => setRole(e.target.value)}>
              {USER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r] ?? r}
                </option>
              ))}
            </select>
            <button
              className="btn pri sm"
              disabled={!name.trim() || !email.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Criando…' : 'Criar'}
            </button>
            <button className="btn ghost sm" onClick={() => setAdding(false)}>
              Cancelar
            </button>
          </div>
        ) : (
          <div className="cfg-new">
            <button className="btn sec sm" onClick={() => setAdding(true)}>
              <Icon name="plus" size={13} />
              Adicionar usuário
            </button>
          </div>
        )}
      </div>
    </>
  )
}
